import { useQuery } from '@apollo/client';
import { Fragment, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { GET_POSTS } from '../../helpers/graphql/posts';
import { getUser } from '../../helpers/token';
import { MUTED } from '../Landing/ui';
import TrailPostCard from './TrailPostCard';

const GRID =
  'tw-grid tw-grid-cols-1 tw-gap-x-6 tw-gap-y-10 sm:tw-grid-cols-2 lg:tw-grid-cols-3';

function SkeletonCards({ count = 3 }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div key={i}>
          <div className="tw-aspect-[16/10] tw-animate-pulse tw-rounded-xl tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
          <div className="tw-mt-3 tw-h-4 tw-w-4/5 tw-animate-pulse tw-rounded tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
          <div className="tw-mt-2 tw-h-3 tw-w-2/5 tw-animate-pulse tw-rounded tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
        </div>
      ))}
    </>
  );
}

// Community posts for a destination or topic. Same client-only persisted
// GET query the old PostGrid ran (never SSR feeds) — only the cards moved
// to the expedition-journal system. The signup CTA is woven INTO the grid
// after the first page: with infinite scroll, anything below it is
// unreachable.
export default function GuidePostGrid({ query, about, cta }) {
  const pageSize = query.limit || 12;

  const [hasMore, setHasMore] = useState(true);
  const [postsLength, setPostsLength] = useState(pageSize);
  // The grid only renders client-side (query is ssr:false), so checking
  // membership here is hydration-safe — unlike in SSR'd markup.
  const [isMember, setIsMember] = useState(true);

  useEffect(() => {
    setIsMember(!!getUser());
  }, []);

  const { data, loading, fetchMore } = useQuery(GET_POSTS, {
    variables: query,
    ssr: false,
  });

  const posts = (data && data.posts) || [];
  if (!loading && posts.length < pageSize && hasMore) setHasMore(false);

  const loadMore = () => {
    if (postsLength === posts.length) {
      fetchMore({
        variables: { offset: posts.length, limit: pageSize },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          if (fetchMoreResult.posts.length < pageSize) setHasMore(false);
          return { ...prev, posts: [...prev.posts, ...fetchMoreResult.posts] };
        },
      });
      setPostsLength(postsLength + pageSize);
    } else if (postsLength !== posts.length + pageSize) {
      setPostsLength(posts.length);
    }
  };

  if (loading && posts.length === 0)
    return (
      <div className={GRID}>
        <SkeletonCards count={6} />
      </div>
    );

  if (posts.length === 0)
    return (
      <div className="tw-rounded-2xl tw-border tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] tw-px-6 tw-py-14 tw-text-center">
        <p className={`${MUTED} tw-m-0 tw-text-[15px]`}>
          No stories yet {about ? `from ${about}` : 'on this topic'} — be the
          first to write one.
        </p>
      </div>
    );

  return (
    <>
      <InfiniteScroll
        loadMore={loadMore}
        hasMore={hasMore}
        threshold={1200}
        loader={
          <div className={`${GRID} tw-mt-10`} key="loader">
            <SkeletonCards />
          </div>
        }
      >
        <div className={GRID}>
          {posts.map((post, i) => (
            <Fragment key={`${post.author.username}-${post.permlink}`}>
              <TrailPostCard post={post} />
              {i === pageSize - 1 && cta && !isMember && (
                <div className="sm:tw-col-span-2 lg:tw-col-span-3">{cta}</div>
              )}
            </Fragment>
          ))}
        </div>
      </InfiniteScroll>

      {/* Short feeds never hit the in-grid slot: CTA after the list ends. */}
      {!hasMore && cta && !isMember && posts.length < pageSize && (
        <div className="tw-mt-10">{cta}</div>
      )}
    </>
  );
}
